import * as R from 'ramda';

import getFilters from '../../selectors/getSelectedFilters';
import getUseRightsholderAttribution from '../../utils/getUseRightsholderAttribution';
import { isFailedToFetchError } from '../../utils/requestErrorHandlers';
import fetchMediaValueReportCached, {
    isMediaValueReportRequestError,
} from './utils/fetchMediaValueReportCached';
import hasFiltersChanged from './utils/hasFiltersChanged';

type AccTypeFilter = {
    groupBy: string[];
    platformPostTypePairs?: { platformId: number }[];
};

type Filters = {
    [key: string]: AccTypeFilter;
};

const accTypeFilters: Filters = {
    'media-type': {
        groupBy: ['mediaType'],
    },
    social: {
        groupBy: ['accountOwnershipType'],
        platformPostTypePairs: [
            { platformId: 1 }, // X
            { platformId: 2 }, // Instagram
            { platformId: 3 }, // Facebook
            { platformId: 4 }, // Youtube
        ],
    },
    broadcast: {
        groupBy: ['accountOwnershipType'],
        platformPostTypePairs: [
            { platformId: 5 }, // Broadcast
        ],
    },
    owned: {
        groupBy: ['socialPlatform'],
        platformPostTypePairs: [
            { platformId: 24 }, // Web
            { platformId: 25 }, // App
        ],
    },
};

const parsers = {
    'media-type': data => {
        const { values } = data;
        return values.map(v => ({
            name: v.mediaTypeName,
            value: v.sponsorMediaValue,
        }));
    },
    social: data => {
        const { values } = data;
        return values.map(v => ({
            name: v.accountOwnershipTypeName,
            value: v.sponsorMediaValue,
        }));
    },
    broadcast: data => {
        const { values } = data;
        return values.map(v => ({
            name: v.accountOwnershipTypeName,
            value: v.sponsorMediaValue,
        }));
    },
    owned: data => {
        const { values } = data;
        return values.map(v => ({
            name: v.socialPlatformName,
            value: v.sponsorMediaValue,
        }));
    },
};

// Gets Media Value by Account Type
const getMVByAccType = store => async (state, accType) => {
    const userClientId = state.user.selectedUserClientId;
    const clientId = state.user.selectedClientId;
    const filters = state.clients[userClientId].filters;
    const client = state.user.clientsById[clientId];
    const useRightsholderAttribution = getUseRightsholderAttribution(
        client,
        clientId,
    );

    // Sets Cards as loading
    const loadingState = R.pipe(
        R.assocPath(
            [
                'clients',
                userClientId,
                'home',
                'mediaValueByAccType',
                accType,
                'isLoading',
            ],
            true,
        ),
        R.assocPath(
            [
                'clients',
                userClientId,
                'home',
                'mediaValueByAccType',
                accType,
                'hasError',
            ],
            false,
        ),
    )(state);
    store.setState(loadingState);
    const changesId = filters.changesId;
    const globFilters = getFilters(filters);
    const query = {
        ...globFilters,
        ...accTypeFilters[accType],
        displayColumns: ['sponsorMediaValue'],
        includeTextTypeExposures: true,
        ...(useRightsholderAttribution
            ? { useRightsholderAttribution: true }
            : {}),
    };
    let result;

    try {
        result = await fetchMediaValueReportCached(query);
    } catch (error) {
        if (
            !isFailedToFetchError(error) &&
            !isMediaValueReportRequestError(error)
        ) {
            throw error;
        }

        if (hasFiltersChanged(store, userClientId, changesId)) return;

        store.setState(
            R.pipe(
                R.assocPath(
                    [
                        'clients',
                        userClientId,
                        'home',
                        'mediaValueByAccType',
                        accType,
                        'isLoading',
                    ],
                    false,
                ),
                R.assocPath(
                    [
                        'clients',
                        userClientId,
                        'home',
                        'mediaValueByAccType',
                        accType,
                        'hasError',
                    ],
                    true,
                ),
            )(store.getState()),
            true,
        );
        return;
    }

    const data = (result as { values: any[] })?.values
        ? parsers[accType](result)
        : [];

    if (hasFiltersChanged(store, userClientId, changesId)) return;

    store.setState(
        R.assocPath(
            ['clients', userClientId, 'home', 'mediaValueByAccType', accType],
            {
                isLoading: false,
                hasError: false,
                values: data,
                changesId,
            },
            store.getState(),
        ),
        true,
    );
};

export default getMVByAccType;
